// Aba "Atualizações" da licitação (05/10/2026): o acompanhamento pelo PNCP e a
// linha do tempo inteira — o que a fonte publicou e o que a equipe registrou.
//
// Pedido do Lucas: "o sistema deve buscar atualizações para nos repassar".
// Todo dia às 08h (e no "Buscar agora") o servidor lê a licitação no PNCP e
// grava o que é novo como evento; aqui se vê o mesmo que chega por e-mail.
// Regra que não se relaxa: FALHA NÃO É "NADA NOVO" — consulta que falhou
// aparece em vermelho com o motivo e desde quando, nunca como "em dia".
import { useCallback, useEffect, useRef, useState } from 'react';
import { App as AntApp, Alert, Button, Card, Empty, Input, Space, Spin, Tag, Timeline, Tooltip, Typography } from 'antd';
import { ReloadOutlined, LinkOutlined, CheckOutlined } from '@ant-design/icons';
import { licApi, erroDe, type Acompanhamento, type EventoLicitacao } from './api';

const { Text } = Typography;

const ICONE: Record<string, string> = {
  acompanhamento_iniciado: '📡', suspensa: '⛔', cancelada: '✖', excluida: '✖', reaberta: '↺', situacao_mudou: '⚑',
  prazo_mudou: '⏰', data_abertura_mudou: '⏰', valor_mudou: '💲', objeto_mudou: '✎',
  anexo_novo: '📄', anexo_removido: '🗑', resultado_publicado: '🏁', ata_publicada: '📜', contrato_publicado: '📜',
  acompanhamento: '📰', proposta_enviada: '✉', proposta_envio_desfeito: '↩', etapa_mudou: '→',
  decisao_participar: '✔', decisao_nao_participar: '✖', importada: '•', documentos_busca: '📎', atualizacao_manual: '🔄',
};
const iconeDe = (ev: EventoLicitacao) =>
  ev.tipo === 'resultado_publicado' && ev.valor_novo === 'ganhamos' ? '🏆' : (ICONE[ev.tipo] || '•');

const quando = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const SITUACAO_ANORMAL = /suspens|revogad|anulad|desert|fracassad|cancelad|exclu[ií]d/i;
// O órgão tirou a publicação do PNCP (o PNCP responde 410) — 14/10/2026.
const EXCLUIDA = /exclu[ií]d/i;

export function AbaAtualizacoes({ oportunidadeId, podeOperar, onVisto }: {
  oportunidadeId: number;
  podeOperar: boolean;
  /** A tela recarrega o selo de "alterações não vistas". */
  onVisto?: () => void;
}) {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState<Acompanhamento | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [esperando, setEsperando] = useState(false);
  const [link, setLink] = useState('');
  const [trocandoLink, setTrocandoLink] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const espera = useRef<ReturnType<typeof setInterval> | null>(null);

  const carregar = useCallback(async () => {
    try { setDados(await licApi.acompanhamento(oportunidadeId)); }
    catch (e) { message.error(erroDe(e)); }
    finally { setCarregando(false); }
  }, [oportunidadeId, message]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { if (espera.current) clearInterval(espera.current); }, []);

  /** A consulta roda em segundo plano: espera a "última consulta" mudar (até ~75 s). */
  function esperarConsulta(antes: string | null) {
    if (espera.current) clearInterval(espera.current);
    setEsperando(true);
    let voltas = 0;
    espera.current = setInterval(async () => {
      voltas++;
      try {
        const d = await licApi.acompanhamento(oportunidadeId);
        if (d.verificado_em && d.verificado_em !== antes) {
          setDados(d);
          setEsperando(false);
          if (espera.current) clearInterval(espera.current);
          if (d.erro) message.warning('O PNCP não respondeu — o motivo está no quadro.');
          else message.success('Consulta ao PNCP feita.');
          onVisto?.();
          return;
        }
      } catch { /* tenta na próxima volta */ }
      if (voltas >= 15) {
        setEsperando(false);
        if (espera.current) clearInterval(espera.current);
        message.info('A consulta ainda não voltou. Atualize a aba em alguns minutos.');
      }
    }, 5000);
  }

  async function buscarAgora() {
    try {
      const antes = dados?.verificado_em || null;
      const r = await licApi.acompanharAgora(oportunidadeId);
      message.info(r.mensagem || 'Consultando o PNCP…');
      esperarConsulta(antes);
    } catch (e) { message.error(erroDe(e)); }
  }

  async function salvarLink() {
    setSalvando(true);
    try {
      const antes = dados?.verificado_em || null;
      const novo = await licApi.definirLinkPncp(oportunidadeId, link.trim());
      setDados(novo);
      setTrocandoLink(false);
      setLink('');
      if (novo.acompanhada) {
        message.success('Link guardado. Consultando o PNCP…');
        esperarConsulta(antes);
      } else {
        message.success('Link guardado.');
      }
    } catch (e) { message.error(erroDe(e)); }
    finally { setSalvando(false); }
  }

  async function marcarVistas() {
    try {
      await licApi.vistoTudo(oportunidadeId);
      await carregar();
      onVisto?.();
    } catch (e) { message.error(erroDe(e)); }
  }

  if (carregando) return <Spin style={{ display: 'block', margin: '32px auto' }} />;
  if (!dados) return <Empty description="Não consegui carregar as atualizações." />;

  const naoVistas = dados.eventos.filter(e => e.material && !e.acknowledged_at).length;
  const semLink = !dados.referencia;
  const campoLink = (
    <Space.Compact style={{ width: '100%', maxWidth: 560 }}>
      <Input value={link} onChange={e => setLink(e.target.value)} placeholder="https://pncp.gov.br/app/editais/CNPJ/2026/123"
        onPressEnter={() => link.trim() && salvarLink()} />
      <Button type="primary" loading={salvando} disabled={!link.trim()} onClick={salvarLink}>Usar este link</Button>
    </Space.Compact>
  );

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card size="small" title="Acompanhamento no PNCP"
        extra={podeOperar && dados.acompanhada ? (
          <Space wrap>
            {!semLink && <Button size="small" icon={<LinkOutlined />} onClick={() => setTrocandoLink(v => !v)}>Trocar link</Button>}
            <Button size="small" icon={<ReloadOutlined />} loading={esperando} disabled={semLink} onClick={buscarAgora}>Buscar agora</Button>
          </Space>
        ) : undefined}>
        {!dados.acompanhada && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            O acompanhamento automático roda enquanto a licitação está aberta no quadro (de Avaliando a Homologação).
          </Text>
        )}
        {semLink ? (
          <Alert type="warning" showIcon style={{ marginBottom: 8 }}
            message="Sem link do PNCP — o sistema não tem onde acompanhar esta licitação"
            description={podeOperar ? <div style={{ marginTop: 6 }}>{campoLink}</div> : 'Peça a quem opera o pipeline para colar o link da licitação no PNCP.'} />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', alignItems: 'center', fontSize: 13 }}>
            <span>Situação no PNCP: {dados.situacao_fonte
              ? <Tag color={SITUACAO_ANORMAL.test(dados.situacao_fonte) ? 'red' : 'blue'} style={{ marginInlineEnd: 0 }}>{dados.situacao_fonte}</Tag>
              : <Text type="secondary">ainda não lida</Text>}</span>
            <span>Resultado: {dados.resultado_publicado ? <b style={{ color: '#0a7f4a' }}>publicado</b> : <Text type="secondary">ainda não publicado</Text>}</span>
            {dados.link_pncp && <a href={dados.link_pncp} target="_blank" rel="noreferrer">Ver no PNCP ↗</a>}
          </div>
        )}
        {!semLink && EXCLUIDA.test(dados.situacao_fonte || '') && (
          <Alert type="warning" showIcon style={{ marginTop: 8 }}
            message="O órgão excluiu esta licitação do PNCP"
            description={`A publicação saiu do ar: pode ter sido cancelada ou vai ser republicada com outro número. Confira com o órgão. ${podeOperar
              ? 'Se sair publicação nova, use "Trocar link" e cole o endereço dela; se acabou, mova para Perdido.'
              : 'Se sair publicação nova, peça a quem opera o pipeline para trocar o link.'}`} />
        )}
        {trocandoLink && podeOperar && <div style={{ marginTop: 8 }}>{campoLink}</div>}
        {!semLink && (dados.erro ? (
          <Alert type="error" showIcon style={{ marginTop: 8 }}
            message={`Não consegui consultar o PNCP${dados.ok_em ? ` desde ${quando(dados.ok_em)}` : ''}`}
            description={`Última tentativa ${quando(dados.verificado_em)}: ${dados.erro}`} />
        ) : (
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            {dados.verificado_em ? <>Última consulta: {quando(dados.verificado_em)} <CheckOutlined style={{ color: '#0a7f4a' }} /></> : 'Ainda não consultado — a primeira consulta sai na próxima rodada das 8h ou no "Buscar agora".'}
          </Text>
        ))}
        <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 11.5 }}>
          Todo dia às 8h o sistema lê esta licitação no PNCP — situação, prazo, documentos novos e resultado. Novidade aparece
          aqui, no sino e por e-mail para o responsável e a lista de cópia das Configurações.
        </Text>
      </Card>

      <Card size="small" title={<span>Linha do tempo {naoVistas > 0 && <Tag color="red" style={{ marginLeft: 6 }}>{naoVistas} não vista{naoVistas > 1 ? 's' : ''}</Tag>}</span>}
        extra={naoVistas > 0 ? <Button size="small" onClick={marcarVistas}>Marcar todas como vistas</Button> : undefined}>
        {!dados.eventos.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nada registrado ainda." />
        ) : (
          <Timeline items={dados.eventos.map(ev => ({
            icon: <span style={{ fontSize: 14 }}>{iconeDe(ev)}</span>,
            color: ev.material && !ev.acknowledged_at ? 'red' : (ev.tipo === 'proposta_enviada' ? 'green' : 'gray'),
            content: (
              <div style={{ fontSize: 12.5 }}>
                <Space size={6} wrap>
                  <span style={{ fontWeight: ev.material ? 600 : 400 }}>{ev.resumo || ev.tipo}</span>
                  {ev.material && !ev.acknowledged_at && <Tag color="red" style={{ margin: 0, fontSize: 10 }}>não vista</Tag>}
                  {ev.acknowledged_at && (
                    <Tooltip title={`Vista em ${quando(ev.acknowledged_at)} por ${ev.acknowledged_by || '—'}`}>
                      <Tag style={{ margin: 0, fontSize: 10 }}>✓ vista</Tag>
                    </Tooltip>
                  )}
                </Space>
                <div style={{ fontSize: 11, color: '#8A8F9C', marginTop: 2 }}>
                  {quando(ev.criado_em)}{ev.criado_por ? ` · ${ev.criado_por}` : ev.origem === 'fonte' ? ' · fonte' : ''}
                </div>
              </div>
            ),
          }))} />
        )}
      </Card>
    </div>
  );
}
